import Button from "../../../shared/components/Button";
import useBodyScrollLock from "../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { formatEventDateRangeLabel } from "../../../shared/utils/dateTime";
import type { Dispatch, SetStateAction } from "react";
import type { AuthUser } from "../../auth/shared/types/auth";
import type { TournamentPairModalState } from "../../tournaments/types/tournamentAdmin";
import type { AvailabilityRestrictionPeriod } from "../../tournamentAvailability/tournamentAvailabilitySettings";
import AdminPlayerSearchInput from "./AdminPlayerSearchInput";
import type { TournamentCategoryEditorItem } from "./TournamentCategoriesEditor";

type Props = {
  modal: TournamentPairModalState;
  saving: boolean;
  categories: TournamentCategoryEditorItem[];
  availableUsers: AuthUser[];
  eventShirtSizesEnabled?: boolean;
  eventShirtSizes?: string[];
  availabilityRestrictionsEnabled?: boolean;
  availabilityRestrictionPeriods?: AvailabilityRestrictionPeriod[];
  availabilityRestrictionAmountCents?: number | null;
  nameByCpf: Record<string, string>;
  paymentMethod: "pix" | "credit_card" | "debit_card";
  onPaymentMethodChange: (
    value: "pix" | "credit_card" | "debit_card",
  ) => void;
  onClose: () => void;
  onSubmit: () => void;
  onChange: Dispatch<SetStateAction<TournamentPairModalState | null>>;
  normalizeCpf: (value: unknown) => string;
  resolvePlayerCpfInput: (value: string) => string;
};

export default function TournamentPairModal({
  modal,
  saving,
  categories,
  availableUsers,
  eventShirtSizesEnabled = false,
  eventShirtSizes = [],
  availabilityRestrictionsEnabled = false,
  availabilityRestrictionPeriods = [],
  availabilityRestrictionAmountCents = 0,
  nameByCpf,
  paymentMethod,
  onPaymentMethodChange,
  onClose,
  onSubmit,
  onChange,
  normalizeCpf,
  resolvePlayerCpfInput,
}: Props) {
  useBodyScrollLock(true);

  const updateModal = (patch: Partial<TournamentPairModalState>) => {
    onChange((prev) =>
      prev
        ? {
            ...prev,
            ...patch,
          }
        : prev,
    );
  };
  const t = siteTexts.admin.registrationsTab;

  const cpf1 = resolvePlayerCpfInput(modal.cpf1);
  const cpf2 = resolvePlayerCpfInput(modal.cpf2);
  const findUserNameByCpf = (cpf: string) =>
    availableUsers.find((user) => normalizeCpf(user.cpf) === cpf)?.name || "";
  const player1Name =
    nameByCpf[cpf1] ||
    findUserNameByCpf(cpf1) ||
    siteTexts.challengeEngine.labels.player1;
  const player2Name =
    nameByCpf[cpf2] ||
    findUserNameByCpf(cpf2) ||
    siteTexts.challengeEngine.labels.player2;
  const player1InputValue = cpf1 ? `${player1Name} - ${cpf1}` : modal.cpf1;
  const player2InputValue = cpf2 ? `${player2Name} - ${cpf2}` : modal.cpf2;
  const getSelectedCpf = (user: AuthUser) => normalizeCpf(user.cpf);

  const fixedPairCategories = categories.filter(
    (category) =>
      String(category.registrationType || "")
        .trim()
        .toLowerCase() === "fixed_pair",
  );
  const selectedCategory =
    fixedPairCategories.find(
      (category) =>
        String(category.categoryKey || "").trim() ===
        String(modal.categoryKey || "").trim(),
    ) || null;
  const normalizeShirtSizes = (values: unknown) =>
    Array.isArray(values)
      ? Array.from(
          new Set(
            values
              .map((value) => String(value || "").trim())
              .filter(Boolean),
          ),
        )
      : [];
  const categoryShirtSizes =
    selectedCategory?.shirtSizesEnabled
      ? normalizeShirtSizes(selectedCategory.shirtSizes)
      : [];
  const availableShirtSizes =
    eventShirtSizesEnabled
      ? categoryShirtSizes.length > 0
        ? categoryShirtSizes
        : normalizeShirtSizes(eventShirtSizes)
      : [];
  const showShirtSizeFields = availableShirtSizes.length > 0;
  const updateShirtSize = (field: "player" | "partner", value: string) => {
    updateModal({
      shirtSizes: {
        ...(modal.shirtSizes || {}),
        [field]: value,
      },
    });
  };
  const availabilityPeriodKey = (period: AvailabilityRestrictionPeriod) =>
    [
      String(period.label || "").trim(),
      String(period.startAt || "").trim(),
      String(period.endAt || "").trim(),
    ].join("|");
  const selectedAvailabilityRestrictionKeys =
    modal.availabilityRestrictionPeriodKeys || [];
  const selectedAvailabilityRestrictionPeriods =
    availabilityRestrictionPeriods.filter((period) =>
      selectedAvailabilityRestrictionKeys.includes(availabilityPeriodKey(period)),
    );
  const shouldShowAvailabilityRestrictions =
    modal.mode === "create" &&
    availabilityRestrictionsEnabled &&
    availabilityRestrictionPeriods.length > 0;
  const availabilityRestrictionTotalCents =
    Math.max(0, Math.round(Number(availabilityRestrictionAmountCents || 0))) *
    selectedAvailabilityRestrictionPeriods.length *
    2;
  const formatCurrency = (cents: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(cents || 0) / 100);

  return (
    <div
      className="admin-modal-backdrop admin-nested-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={modal.mode === "edit" ? "Editar dupla" : "Adicionar dupla"}
    >
      <div
        className={`admin-modal admin-tournament-modal admin-tournament-pair-modal${
          shouldShowAvailabilityRestrictions
            ? " admin-tournament-pair-modal--with-restrictions"
            : ""
        }`}
      >
        <div className="admin-modal-header">
          <h3>{modal.mode === "edit" ? "Editar dupla" : "Adicionar dupla"}</h3>

          <Button
            type="button"
            variant="pill"
            onClick={onClose}
            disabled={saving}
          >
            Fechar
          </Button>
        </div>

        <div className="admin-form-grid">
          <label
            className={`field admin-tournament-pair-category-field${
              modal.mode === "edit"
                ? " admin-tournament-pair-category-field--wide"
                : ""
            }`}
          >
            <span>Categoria</span>
            <select
              value={modal.categoryKey}
              onChange={(event) =>
                updateModal({
                  categoryKey: event.target.value,
                  shirtSizes: { player: "", partner: "" },
                })
              }
            >
              {fixedPairCategories.map((category) => (
                <option
                  key={`pair-modal-category-${category.categoryKey}`}
                  value={String(category.categoryKey || "").trim()}
                >
                  {String(category.categoryName || category.categoryKey).trim()}
                </option>
              ))}
            </select>
          </label>

          {shouldShowAvailabilityRestrictions && (
            <div className="admin-tournament-pair-restrictions">
              <div>
                <strong>Restrição de disponibilidade</strong>
                <span>Marque os períodos em que a dupla não poderá jogar.</span>
              </div>
              <div
                className="admin-tournament-pair-restriction-options"
                role="group"
                aria-label="Restrições de disponibilidade"
              >
                {availabilityRestrictionPeriods.map((period) => {
                  const key = availabilityPeriodKey(period);
                  const checked =
                    selectedAvailabilityRestrictionKeys.includes(key);
                  return (
                    <label
                      key={key}
                      className={`admin-tournament-pair-restriction-option${
                        checked ? " is-selected" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => {
                          const nextKeys = event.target.checked
                            ? selectedAvailabilityRestrictionKeys.includes(key)
                              ? selectedAvailabilityRestrictionKeys
                              : [...selectedAvailabilityRestrictionKeys, key]
                            : selectedAvailabilityRestrictionKeys.filter(
                                (item) => item !== key,
                              );
                          updateModal({
                            availabilityRestrictionPeriodKeys: nextKeys,
                          });
                        }}
                        disabled={saving}
                      />
                      <span>
                        <strong>{period.label}</strong>
                        <small>
                          {formatEventDateRangeLabel(
                            period.startAt,
                            period.endAt,
                          )}
                        </small>
                      </span>
                    </label>
                  );
                })}
              </div>
              {availabilityRestrictionTotalCents > 0 && (
                <small>
                  Adicional na cobrança:{" "}
                  {formatCurrency(availabilityRestrictionTotalCents)}
                </small>
              )}
            </div>
          )}

          <div className="field admin-tournament-pair-player-field admin-tournament-pair-player-field--one">
            <span>Jogador 1</span>
            <AdminPlayerSearchInput
              availableUsers={availableUsers}
              excludeCpf={cpf2}
              getSelectedValue={getSelectedCpf}
              id="admin-tournament-pair-player-1"
              onValueChange={(value) => updateModal({ cpf1: value })}
              placeholder={siteTexts.challengeEngine.labels.player1Placeholder}
              value={player1InputValue}
            />
          </div>

          <div className="field admin-tournament-pair-player-field admin-tournament-pair-player-field--two">
            <span>Jogador 2</span>
            <AdminPlayerSearchInput
              availableUsers={availableUsers}
              excludeCpf={cpf1}
              getSelectedValue={getSelectedCpf}
              id="admin-tournament-pair-player-2"
              onValueChange={(value) => updateModal({ cpf2: value })}
              placeholder={siteTexts.challengeEngine.labels.player2Placeholder}
              value={player2InputValue}
            />
          </div>

          {modal.mode === "create" && (
            <label className="field admin-tournament-payment-method-field">
              <span>{t.paymentMethod}</span>
              <select
                value={paymentMethod}
                onChange={(event) =>
                  onPaymentMethodChange(
                    event.target.value as
                      | "pix"
                      | "credit_card"
                      | "debit_card",
                  )
                }
              >
                <option value="pix">PIX</option>
                <option value="debit_card">{t.debitCard}</option>
                <option value="credit_card">{t.creditCard}</option>
              </select>
            </label>
          )}

          {showShirtSizeFields && (
            <>
              <label className="field admin-tournament-pair-shirt-field">
                <span>Camiseta jogador 1</span>
                <select
                  value={modal.shirtSizes?.player || ""}
                  onChange={(event) =>
                    updateShirtSize("player", event.target.value)
                  }
                >
                  <option value="">Selecione</option>
                  {availableShirtSizes.map((size) => (
                    <option key={`shirt-player-${size}`} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field admin-tournament-pair-shirt-field">
                <span>Camiseta jogador 2</span>
                <select
                  value={modal.shirtSizes?.partner || ""}
                  onChange={(event) =>
                    updateShirtSize("partner", event.target.value)
                  }
                >
                  <option value="">Selecione</option>
                  {availableShirtSizes.map((size) => (
                    <option key={`shirt-partner-${size}`} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
        </div>

        <div className="form-linha nomes-dupla">
          <span>{cpf1 ? `${player1Name} - ${cpf1}` : player1Name}</span>
          <span>{cpf2 ? `${player2Name} - ${cpf2}` : player2Name}</span>
        </div>

        <div className="admin-modal-actions">
          <Button
            type="button"
            variant="pill"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </Button>

          <Button
            type="button"
            variant="primary"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? "Salvando..." : "Salvar dupla"}
          </Button>
        </div>
      </div>
    </div>
  );
}
